import { ApplicationSchema } from '@/features/application/application.type';
import { useAuthStore } from '@/features/auth/auth-store';
import { apiWrapper } from '@/shared/use-api';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import {
  SubscriptionBillingPortalSchema,
  SubscriptionSchema,
} from './subscription.type';

const getSubscriptionsQueryOptions = ({
  accessToken,
  checkoutSuccessful,
  orgId,
  page = 0,
  pageSize = 10,
}: {
  accessToken: string | undefined;
  checkoutSuccessful: boolean;
  orgId: string | undefined;
  page?: number;
  pageSize?: number;
}) => {
  return queryOptions({
    queryKey: ['subscriptions', orgId, page, pageSize],
    queryFn: () => {
      const params = new URLSearchParams({
        page: page.toString(),
        size: pageSize.toString(),
      });

      return apiWrapper({
        accessToken,
        endpoint: `/admin/organisation/${orgId}/subscription?${params.toString()}`,
        method: 'GET',
        responseSchema: SubscriptionSchema,
        isPaginated: true,
      });
    },
    enabled: !!accessToken && !!orgId,
    refetchInterval: (query) => {
      const data = query.state.data;

      const usageSubscription = data?.content.find(
        (subscription) =>
          subscription.type === 'Usage' && !subscription.trial,
      );
      const hasActiveUsageSubscription = usageSubscription?.active;

      if (checkoutSuccessful && !hasActiveUsageSubscription) {
        return 2000;
      }

      return false;
    },
  });
};

export const useSubscriptionsQuery = ({
  page = 0,
  pageSize = 50,
}: {
  page?: number;
  pageSize?: number;
}) => {
  const { accessToken, organizationId } = useAuthStore();
  const checkoutSuccessfulSearchParam = useSearch({
    from: '/api-key',
    shouldThrow: false,
  });

  return useQuery(
    getSubscriptionsQueryOptions({
      accessToken,
      checkoutSuccessful:
        checkoutSuccessfulSearchParam?.checkoutSuccessful ?? false,
      orgId: organizationId,
      page,
      pageSize,
    }),
  );
};

const getBillingPortalLinkQueryOptions = ({
  accessToken,
  enabled,
  orgId,
  subscriptionId,
}: {
  accessToken: string | undefined;
  enabled?: boolean;
  orgId: string | undefined;
  subscriptionId: string;
}) => {
  return queryOptions({
    queryKey: ['billingPortal', orgId, subscriptionId],
    queryFn: () => {
      return apiWrapper({
        accessToken,
        endpoint: `/admin/organisation/${orgId}/subscription/${subscriptionId}/billingPortal`,
        isPaginated: false,
        method: 'GET',
        responseSchema: SubscriptionBillingPortalSchema,
        searchParams: {
          returnUrl: window.location.origin,
        },
      });
    },
    enabled: enabled && !!accessToken && !!orgId && !!subscriptionId,
  });
};

export const useBillingPortalLinkQuery = ({
  subscriptionId,
  enabled = true,
}: {
  subscriptionId: string;
  enabled?: boolean;
}) => {
  const { accessToken, organizationId } = useAuthStore();

  return useQuery(
    getBillingPortalLinkQueryOptions({
      accessToken,
      enabled,
      orgId: organizationId,
      subscriptionId,
    }),
  );
};

const getAppsAssignedToSubscriptionQueryOptions = ({
  accessToken,
  orgId,
  subscriptionId,
}: {
  accessToken: string | undefined;
  orgId: string | undefined;
  subscriptionId: string;
}) => {
  return queryOptions({
    queryKey: ['appsAssignedToSubscription', orgId, subscriptionId],
    queryFn: () => {
      return apiWrapper({
        endpoint: `/admin/organisation/${orgId}/subscription/${subscriptionId}/assignment/app`,
        method: 'GET',
        accessToken,
        responseSchema: ApplicationSchema.array(),
        isPaginated: false,
      });
    },
    enabled: !!accessToken && !!orgId && !!subscriptionId,
  });
};

export const useAppsAssignedToSubscriptionQuery = ({
  subscriptionId,
}: {
  subscriptionId: string;
}) => {
  const { accessToken, organizationId } = useAuthStore();

  return useQuery({
    ...getAppsAssignedToSubscriptionQueryOptions({
      accessToken,
      orgId: organizationId,
      subscriptionId,
    }),
    enabled: !!accessToken && !!organizationId && !!subscriptionId,
  });
};
